/**
 * A aba "Chuva" do painel "Rastreio e previsão": liga o rastreio das áreas de
 * chuva do GSMaP, mostra os números da hora que casa com a imagem e a lista
 * das áreas, da maior para a menor (o toque leva o mapa até ela e abre a
 * ficha). A chuva do GSMaP por baixo, com a intensidade, fica a um toque.
 */
import clsx from 'clsx';
import { ArrowUp, ChevronDown, History } from 'lucide-react';
import { useEffect, useId, useMemo, useRef } from 'react';
import type OlMap from 'ol/Map';
import { useGsmapStore } from '../gsmap/store';
import { quadroParaInstante } from '../gsmap/tempo';
import { formatDecimal, formatInt } from '../lib/format';
import { formatTimeBrt, MINUTE_MS } from '../lib/time';
import { openPopupAt } from '../map/popupControl';
import { painelSobreOMapa } from '../map/viewControl';
import { useGoesStore } from '../state/goes';
import { shownTimeOf, useTimeStore } from '../state/time';
import { Aviso, Interruptor, Numeros, Traco } from '../tracking/controles';
import { pontoCardeal } from '../tracking/TrackingPopup';
import { COR_CHUVA, COR_SELECAO, TRACO_CHUVA, TRACO_CHUVA_SELECIONADO } from '../tracking/tracos';
import { useNow } from '../ui/hooks';
import type { QuadroChuva } from './api';
import { FichaDaChuva, mmh } from './FichaDaChuva';
import { areaDaChuva } from './layer';
import { useRastreioChuvaStore, type EstadoNoQuadro, type SistemaChuvaNaLista } from './store';
import { carregarRastreioChuva, fimDoUltimo, refreshRastreioChuva } from './sync';

/** Áreas na lista antes de "ver todas". */
const LISTA_CURTA = 6;

export const DESCRICAO_DO_RASTREIO_DA_CHUVA = 'Áreas com chuva de 0,1 mm/h ou mais (GSMaP), de 30 em 30 min.';

/** Área grande em poucos algarismos: 2,6 mi · 754 mil · 8.400. */
export function areaCurta(km2: number): string {
  if (km2 >= 1_000_000) return `${formatDecimal(km2 / 1_000_000, 1)} mi`;
  if (km2 >= 100_000) return `${formatInt(Math.round(km2 / 1000))} mil`;
  return formatInt(km2);
}

/** A hora do GSMaP por extenso curto: "20:30–21:30". */
export const horaDoQuadro = (q: Pick<QuadroChuva, 'ini' | 'fim'>) => `${formatTimeBrt(q.ini)}–${formatTimeBrt(q.fim)}`;

/** A frase da hora na tela (o leitor de tela a ouve; fora do regime, ela é o que se vê). */
export function fraseDaChuva(q: QuadroChuva | null, estado: EstadoNoQuadro, ultimo: QuadroChuva | null, instante: number | null): string {
  if (instante === null) return '—';
  if (!q) {
    return ultimo && instante > ultimo.fim
      ? `O rastreio da chuva desta imagem ainda não chegou (a hora mais nova é ${horaDoQuadro(ultimo)}): o mapa mostra só a imagem.`
      : 'Sem hora do GSMaP perto desta imagem: o mapa mostra só a imagem.';
  }
  if (estado === 'carregando') return 'Baixando as áreas de chuva desta hora…';
  if (estado === 'falhou') return 'As áreas de chuva desta hora não carregaram.';
  if (q.n === 0) return `Nenhuma área com chuva de 0,1 mm/h ou mais na hora ${horaDoQuadro(q)}.`;
  const areas = q.n === 1 ? '1 área de chuva' : `${formatInt(q.n)} áreas de chuva`;
  return `${areas} na hora ${horaDoQuadro(q)}, ${formatInt(q.km2)} km² no total${q.pmax !== null ? `; a mais forte a ${mmh(q.pmax)}` : ''}.`;
}

/** Mais de 75 min sem hora nova (o normal é 5–15 depois do fim da hora): avisa. */
export function avisoDaChuva(fim: number | null, agora: number = Date.now()): string | null {
  if (fim === null) return null;
  const min = (agora - fim) / MINUTE_MS;
  if (min < 75) return null;
  const quanto = min >= 120 ? `${Math.floor(min / 60)} h` : `${Math.round(min)} min`;
  return `Rastreio da chuva sem hora nova há ${quanto}. As imagens mais recentes aparecem sem ele.`;
}

/** Leva o mapa até a área e abre a ficha dela. */
export function irAChuva(map: OlMap, item: SistemaChuvaNaLista, mobile: boolean): void {
  const view = map.getView();
  view.cancelAnimations();
  view.fit(item.extensao, {
    padding: mobile ? [80, 24, 96, 24] : [96, 420, 64, 360 + painelSobreOMapa()],
    duration: 650,
    maxZoom: 7,
    callback: (completo) => {
      if (completo) openPopupAt(item.ponto, <FichaDaChuva sistema={item.sistema} />, { movel: true, evitar: item.sistema.uid !== null ? areaDaChuva(item.sistema.uid) : undefined });
    },
  });
}

function Lista(props: { itens: readonly SistemaChuvaNaLista[]; onPick: (item: SistemaChuvaNaLista) => void }) {
  const aberta = useRastreioChuvaStore((s) => s.listaAberta);
  const todos = useRastreioChuvaStore((s) => s.listaInteira);
  const selecao = useRastreioChuvaStore((s) => s.selecao);
  const { setListaAberta, setListaInteira } = useRastreioChuvaStore.getState();
  const id = useId();
  const caixa = useRef<HTMLDivElement>(null);
  const pedida = useRef(false);
  // Aberta pelo usuário, a lista sobe para o topo da área rolável: os itens nasceriam abaixo da dobra.
  useEffect(() => {
    if (!aberta || !pedida.current) return;
    pedida.current = false;
    const calmo = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    caixa.current?.scrollIntoView({ block: 'start', behavior: calmo ? 'auto' : 'smooth' });
  }, [aberta]);
  const maior = props.itens[0]?.sistema.km2 ?? 0;
  const itens = todos ? props.itens : props.itens.slice(0, LISTA_CURTA);
  if (!props.itens.length) return null;
  return (
    <div ref={caixa} data-lista-da-chuva className="scroll-mt-2 overflow-hidden rounded-lg border border-ink-700/70">
      <button
        type="button"
        aria-expanded={aberta}
        aria-controls={id}
        onClick={() => {
          pedida.current = !aberta;
          setListaAberta(!aberta);
        }}
        className="flex min-h-10 w-full items-center gap-2 bg-ink-800/60 px-2.5 text-left text-xs font-medium text-ink-100 hover:bg-ink-700/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:min-h-12"
      >
        <span className="min-w-0 flex-1 truncate">
          Áreas nesta hora <span className="text-ink-400">·</span> <span className="tabular-nums">{formatInt(props.itens.length)}</span>
        </span>
        <span className="shrink-0 text-[11px] font-normal text-ink-300">{aberta ? 'Recolher' : 'Ver a lista'}</span>
        <ChevronDown className={clsx('size-4 shrink-0 text-ink-300 transition-transform', aberta && 'rotate-180')} aria-hidden />
      </button>
      <div id={id} hidden={!aberta}>
        {aberta && (
          <>
            <p className="border-t border-ink-700/60 px-2.5 py-1 text-[11px] text-ink-300">Da maior para a menor; cada uma leva o mapa até ela.</p>
            <ul className="divide-y divide-ink-700/60 border-t border-ink-700/60">
              {itens.map((item) => {
                const s = item.sistema;
                const atual = s.uid !== null && s.uid === selecao;
                // A barra embaixo do número: o tamanho em relação à maior da hora.
                const fracao = maior > 0 && s.km2 !== null ? Math.max(0.03, s.km2 / maior) : 0;
                return (
                  <li key={String(s.uid)}>
                    <button
                      type="button"
                      aria-current={atual ? 'true' : undefined}
                      onClick={() => props.onPick(item)}
                      className={clsx(
                        'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-ink-700/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:min-h-11',
                        atual ? 'bg-ink-700/80' : 'bg-ink-800/40',
                      )}
                      style={atual ? { boxShadow: `inset 3px 0 0 ${COR_SELECAO}` } : undefined}
                    >
                      <span className="w-10 shrink-0 font-semibold text-white tabular-nums">{s.uid ?? '—'}</span>
                      <span className="relative min-w-0 flex-1">
                        <span className="block truncate text-ink-200 tabular-nums">
                          {s.km2 !== null ? `${formatInt(s.km2)} km²` : '—'}
                          {s.pmax !== null && <span className="text-ink-400"> · máx. {mmh(s.pmax)}</span>}
                        </span>
                        {/* Fora do fluxo: o número, a área e a velocidade ficam na mesma linha. */}
                        <span className="absolute inset-x-0 top-full mt-0.5 block h-[3px] rounded-full bg-ink-700" aria-hidden>
                          <span className="block h-full rounded-full" style={{ width: `${fracao * 100}%`, background: COR_CHUVA, opacity: 0.75 }} />
                        </span>
                      </span>
                      {s.vel !== null && (
                        <span className="flex shrink-0 items-center gap-1 text-ink-200 tabular-nums" title={s.rumo !== null ? `para ${pontoCardeal(s.rumo)}` : undefined}>
                          {formatInt(s.vel)} km/h
                          {s.rumo !== null && s.vel > 0 && <ArrowUp className="size-3.5" style={{ transform: `rotate(${s.rumo}deg)`, color: COR_CHUVA }} aria-hidden />}
                        </span>
                      )}
                      {atual && <span className="sr-only">(selecionada)</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
            {props.itens.length > LISTA_CURTA && (
              <button
                type="button"
                onClick={() => setListaInteira(!todos)}
                className="flex min-h-9 w-full items-center border-t border-ink-700/60 px-2.5 text-[11px] font-medium text-mata-300 hover:underline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:min-h-11"
              >
                {todos ? 'Ver só as maiores' : `Ver as ${props.itens.length} áreas`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** A chuva do GSMaP por baixo dos contornos: opção secundária, como os núcleos das nuvens. */
function ChuvaPorBaixo() {
  const id = useId();
  const ligada = useGsmapStore((s) => s.visivel);
  return (
    <div className="ml-3.5 border-l border-ink-600 pl-2.5">
      <button
        type="button"
        role="switch"
        aria-checked={ligada}
        aria-label="Intensidade da chuva"
        aria-describedby={id}
        onClick={() => useGsmapStore.getState().setVisivel(!ligada)}
        className="flex min-h-9 w-full items-center gap-2 rounded-md py-1 pr-1 pl-1 text-left hover:bg-ink-800/70 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400 pointer-coarse:min-h-11"
      >
        {/* A escala da camada da chuva, em miniatura: do azul-claro ao magenta. */}
        <span aria-hidden className="h-2 w-[15px] shrink-0 rounded-sm" style={{ background: 'linear-gradient(90deg,#c6dbef,#3182bd,#54278f,#c51b8a)', opacity: ligada ? 1 : 0.4 }} />
        <span className="min-w-0 flex-1 leading-tight">
          <span className={clsx('block truncate text-xs', ligada ? 'text-ink-100' : 'text-ink-200')}>Intensidade da chuva</span>
          <span id={id} className="block truncate text-[11px] leading-snug text-ink-300">
            Por baixo dos contornos, em mm/h.
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5" aria-hidden>
          <span className={clsx('text-[11px] leading-none', ligada ? 'font-semibold text-mata-300' : 'text-ink-400')}>{ligada ? 'Ligada' : 'Desligada'}</span>
          <span className={clsx('relative inline-flex h-4 w-7 items-center rounded-full transition-colors', ligada ? 'bg-mata-500' : 'bg-ink-600')}>
            <span className={clsx('inline-block size-3 rounded-full bg-white shadow transition-transform', ligada ? 'translate-x-3.5' : 'translate-x-0.5')} />
          </span>
        </span>
      </button>
    </div>
  );
}

export function PainelDaChuva(props: { onPick: (item: SistemaChuvaNaLista) => void }) {
  const ligado = useRastreioChuvaStore((s) => s.ligado);
  const situacao = useRastreioChuvaStore((s) => s.situacao);
  const indice = useRastreioChuvaStore((s) => s.indice);
  const noQuadro = useRastreioChuvaStore((s) => s.noQuadro);
  const sistemas = useRastreioChuvaStore((s) => s.sistemas);
  const satelite = useGoesStore((s) => s.layers.some((l) => l.visible));
  const instante = useTimeStore((s) => shownTimeOf(s));
  const frames = useTimeStore((s) => s.frames);
  const now = useNow(60_000);

  const quadros = indice?.quadros ?? [];
  const meios = useMemo(() => quadros.map((q) => q.t), [quadros]);
  const q = quadroParaInstante(quadros, meios, instante);
  const ultimo = quadros.length ? quadros[quadros.length - 1]! : null;
  // A imagem mais nova costuma chegar antes da hora do GSMaP: um toque leva à última imagem que tem rastreio.
  const indiceDoUltimo = useMemo(() => {
    if (!ultimo) return -1;
    for (let i = frames.length - 1; i >= 0; i--) if (quadroParaInstante(quadros, meios, frames[i]!) === ultimo) return i;
    return -1;
  }, [frames, quadros, meios, ultimo]);
  const irAoUltimo = !q && indiceDoUltimo >= 0 && instante !== null && ultimo !== null && instante > ultimo.fim;
  const aviso = ligado ? avisoDaChuva(fimDoUltimo(indice), now) : null;
  const frase = fraseDaChuva(q, noQuadro, ultimo, instante);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Interruptor nome="Rastreio da chuva" descricao={DESCRICAO_DO_RASTREIO_DA_CHUVA} ligado={ligado} traco={TRACO_CHUVA} onChange={(on) => useRastreioChuvaStore.getState().setLigado(on)} />
        <ChuvaPorBaixo />
      </div>

      {ligado && !satelite && <Aviso>As imagens do satélite estão desligadas: o rastreio da chuva aparece junto com elas.</Aviso>}
      {aviso && <Aviso>{aviso}</Aviso>}
      {ligado && situacao.estado === 'error' && !indice && (
        <Aviso>
          Não deu para baixar a lista de horas do rastreio da chuva: {situacao.erro}{' '}
          <button type="button" className="font-semibold underline" onClick={() => void carregarRastreioChuva(true)}>
            Tentar de novo
          </button>
        </Aviso>
      )}
      {ligado && situacao.estado === 'loading' && !indice && <p className="text-xs text-ink-300">Baixando a lista de horas…</p>}

      {ligado && indice && satelite && (
        <>
          {q && noQuadro === 'ok' && q.n > 0 ? (
            <Numeros
              cor={COR_CHUVA}
              frase={frase}
              titulo={
                <>
                  Hora {horaDoQuadro(q)}
                  <span className="text-ink-500">·</span>
                  <span className="min-w-0 truncate text-ink-400">{q.p === 's' ? 'GSMaP só satélite' : 'GSMaP ajustado por pluviômetros'}</span>
                </>
              }
              numeros={[
                { rotulo: 'Áreas', valor: formatInt(q.n) },
                { rotulo: 'Área total', valor: areaCurta(q.km2), unidade: 'km²' },
                { rotulo: 'Mais forte', valor: q.pmax !== null ? formatDecimal(q.pmax, q.pmax < 10 ? 1 : 0) : '—', unidade: 'mm/h' },
              ]}
            />
          ) : (
            <div className="rounded-lg border border-ink-700/70 bg-ink-800/50 px-2.5 py-1.5 text-xs leading-snug text-ink-200" role="status">
              {frase}
              {noQuadro === 'falhou' && (
                <>
                  {' '}
                  <button type="button" className="font-semibold text-mata-300 underline focus-visible:outline-2 focus-visible:outline-mata-400" onClick={refreshRastreioChuva}>
                    Tentar de novo
                  </button>
                </>
              )}
              {irAoUltimo && (
                <button
                  type="button"
                  onClick={() => useTimeStore.getState().setIndex(indiceDoUltimo)}
                  className="mt-1 flex items-center gap-1 font-semibold text-mata-300 hover:underline focus-visible:outline-2 focus-visible:outline-mata-400"
                >
                  <History className="size-3.5" aria-hidden />
                  Ir para a última hora rastreada ({horaDoQuadro(ultimo)})
                </button>
              )}
            </div>
          )}
          {noQuadro === 'ok' && <Lista itens={sistemas} onPick={props.onPick} />}
        </>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-ink-700/60 pt-2 text-[11px] text-ink-300">
        <span className="flex items-center gap-1.5">
          <Traco traco={TRACO_CHUVA} />
          área de chuva (≥ {formatDecimal(indice?.limiar ?? 0.1, 1)} mm/h)
        </span>
        <span className="flex items-center gap-1.5">
          <Traco traco={TRACO_CHUVA_SELECIONADO} />
          área com a ficha aberta
        </span>
      </div>
    </div>
  );
}
